import { DayLight, NtpInterval, NtpSyncEnable, TimeType } from '@EcamModel/model/PTZSysDateTime';
import { Divider, Stack, Typography } from '@mui/material';
import { useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import color from '@theme/Colors';
import { useEffect, useState } from 'react';
import { formatISODate, formatUKTime, getTimezoneDescription } from 'src/helpers';
import { DataSectionHasSubItem, SectionHasSubItemHeader, SectionNotSubItem } from './BasicInfoMilesightCameraPTZ';
import useMileSightPTZ, { SectionsCollapsePTZ } from '@pages/cameras/hook/useMilesightPTZ';

const dayLightMapping: { [key in DayLight]: string } = {
    [DayLight.Disabled]: 'Disabled',
    [DayLight.Automatic]: 'Automatic',
};

const timeTypeMapping: { [key in TimeType]: string } = {
    [TimeType.NTP]: 'NTP server',
    [TimeType.Computer]: 'Synchronize with computer time',
    [TimeType.Manual]: 'Manual',
};

const ntpSyncEnableMapping: { [key in NtpSyncEnable]: string } = {
    [NtpSyncEnable.Disable]: 'Disable',
    [NtpSyncEnable.Enable]: 'Enable',
};

const ntpIntervalMapping: { [key in NtpInterval]: string } = {
    [NtpInterval.OneHour]: '60',
    [NtpInterval.TwoHours]: '120',
    [NtpInterval.FiveHours]: '300',
    [NtpInterval.TenHours]: '600',
    [NtpInterval.OneDay]: '1440',
    [NtpInterval.TwoDays]: '2880',
    [NtpInterval.FiveDays]: '7200',
    [NtpInterval.TenDays]: '14400',
    [NtpInterval.ThirtyDays]: '43200',
};

type Props = {
    isOpenCollapse: { [key in SectionsCollapsePTZ]: boolean };
    handleToggleCollapse: (section: SectionsCollapsePTZ) => void;
};

export default function DateAndTimeMilesightsCameraPTZ(props: Props) {
    const { camera } = useCameraDetailContext();
    const { isOpenCollapse, handleToggleCollapse } = props;
    const PTZSysDateTime = camera?.PTZCamera?.PTZCameraConfig?.SysDateTime;
    const [time, setTime] = useState(formatUKTime(new Date()));

    useEffect(() => {
        const intervalId = setInterval(() => {
            setTime(formatUKTime(new Date()));
        }, 1000);

        return () => clearInterval(intervalId);
    }, []);

    const statusList_CurrentSystemTime: DataSectionHasSubItem[] = [
        {
            label: 'Date:',
            value: <Typography>{formatISODate(new Date())}</Typography>,
        },
        {
            label: 'Time:',
            value: <Typography>{time}</Typography>,
        },
    ];

    const statusList_SetTheSystemTime: DataSectionHasSubItem[] = [
        {
            label: 'Time zone:',
            value: (
                <Typography color={color.secondary}>{getTimezoneDescription(PTZSysDateTime?.TimeZoneTz!)}</Typography>
            ),
        },
        {
            label: 'Daylight saving time:',
            value: <Typography>{dayLightMapping[PTZSysDateTime?.DayLight!]}</Typography>,
        },
        {
            label: 'Synchronize mode:',
            value: <Typography>{timeTypeMapping[PTZSysDateTime?.TimeType!]}</Typography>,
        },
        {
            label: 'Server address:',
            value: <Typography>{PTZSysDateTime?.NtpServer!}</Typography>,
        },
        {
            label: 'NTP sync:',
            value: <Typography>{ntpSyncEnableMapping[PTZSysDateTime?.NtpSyncEnable!]}</Typography>,
        },
        {
            label: 'Interval:',
            value: <Typography>{`${ntpIntervalMapping[PTZSysDateTime?.NtpInterval!]} min (1~43200)`}</Typography>,
        },
    ];

    const handleEdit = () => {
        console.log('Edit button clicked');
    };
    return (
        <Stack id="currentSystemTime" border={'1px solid #E4E7EC'} borderRadius={2} flex={1}>
            <SectionHasSubItemHeader id="" title="Date & Time" onEdit={handleEdit} />

            <Divider
                orientation="vertical"
                sx={{
                    borderBottomWidth: 1,
                    borderColor: '#E4E7EC',
                    mx: 2,
                    mb: 2,
                }}
            />

            <SectionNotSubItem
                header="Set the System Time"
                statusList={statusList_SetTheSystemTime}
                isOpen={isOpenCollapse.setTheSystemTime}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapsePTZ.SetTheSystemTime);
                }}
            />

            <SectionNotSubItem
                name="setTheSystemTime"
                header="Current system time"
                statusList={statusList_CurrentSystemTime}
                isOpen={isOpenCollapse.currentSystemTime}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapsePTZ.CurrentSystemTime);
                }}
            />
        </Stack>
    );
}
